import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { TipTargeting } from '../utils/tipTargeting';
import { canPerformAction } from '../utils/permissionUtils';
import TipContentRenderer from './TipContentRenderer';
import OffScoreTipDialog from './OffScoreTipDialog';
import '../styles/tips.css';

const OffScoreTipsDisplay = ({ musicxmlId, isCollapsed = true, onToggle, refreshTrigger = 0 }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [showTipDialog, setShowTipDialog] = useState(false);
  const [editingTip, setEditingTip] = useState(null);


  // Load off-score tips when component mounts or musicxml changes
  useEffect(() => {
    if (user?.id) {
      loadOffScoreTips();
    }
  }, [user, musicxmlId, refreshTrigger]);

  const loadOffScoreTips = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      // For off-score tips, we want both general tips (no file) AND tips specific to this score
      const queryParams = new URLSearchParams({
        operation: 'getTips',
        userId: user.id,
        type: 'off-score',
        userRole: user.role || 'user'
      });
      
      // Add musicxmlId to get both general tips (null) and file-specific tips
      if (musicxmlId) {
        queryParams.set('musicxmlId', musicxmlId);
      }
      
      // console.log('OffScoreTipsDisplay: API call params:', queryParams.toString());
      // console.log('OffScoreTipsDisplay: Current musicxmlId:', musicxmlId);
      
      const response = await fetch(`${API_BASE}/api/tips?${queryParams}`);
      
      if (response.ok) {
        const tipsData = await response.json();
       // console.log('OffScoreTipsDisplay: Raw API response:', tipsData);
       // console.log('OffScoreTipsDisplay: Current user:', { id: user.id, email: user.email, role: user.role });
        
        // Apply client-side filtering for general display (excludes creator's own tips unless targeted)
        const filteredTips = TipTargeting.filterTipsForGeneralDisplay(tipsData, user, {
          currentPart: null // TODO: Get current part from context when available
        });
        
        //console.log('OffScoreTipsDisplay: Filtered tips:', filteredTips);
        setTips(filteredTips);
      } else {
        console.error('Failed to load off-score tips:', response.status);
      }
    } catch (error) {
      console.error('Error loading off-score tips:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setCollapsed(!collapsed);
    if (onToggle) {
      onToggle(!collapsed);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getCreatorName = (tip) => {
    if (tip.creator_user_id === user?.id) {
      return t('common.you');
    }
    return tip.creator_display_name || tip.creator_email || tip.creator_user_id;
  };

  const getTargetingDescription = (tip) => {
    if (tip.target_rules?.everyone) {
      return t('tips.forEveryone');
    }
    if (tip.target_rules?.roles?.length > 0) {
      return `${t('tips.forRoles')}: ${tip.target_rules.roles.join(', ')}`;
    }
    if (tip.target_user_ids?.length > 0) {
      return `${tip.target_user_ids.length} ${t('tips.specificUsers')}`;
    }
    return t('tips.forEveryone');
  };

  const handleCreateTip = () => {
    setEditingTip(null);
    setShowTipDialog(true);
  };

  const handleEditTip = (tip) => {
    setEditingTip(tip);
    setShowTipDialog(true);
  };

  const handleTipSaved = async (tipData) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      if (editingTip) {
        // Update existing tip
        const response = await fetch(`${API_BASE}/api/tips?operation=updateTip&userId=${user.id}&id=${editingTip.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tipData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update tip');
        }
      } else {
        // Create new tip
        const response = await fetch(`${API_BASE}/api/tips?operation=createTip&userId=${user.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tipData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create tip');
        }
      }

      // Reload tips to show the changes
      loadOffScoreTips();
      
    } catch (error) {
      console.error('Error saving tip:', error);
      throw error;
    }
  };

  // Don't render if user is not logged in
  if (!user) {
    return null;
  }

  const canCreateTips = canPerformAction(user, 'create-tip');

  // Hide the section entirely when there are no tips for this piece —
  // unless the user can create tips (they need the header's add button)
  if (tips.length === 0 && !canCreateTips) {
    return null;
  }

  return (
    <div className="off-score-tips" style={{
      backgroundColor: '#2d3748',
      borderRadius: '8px',
      marginTop: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: '1px solid #4a5568',
      overflow: 'hidden'
    }}>
      <div className="off-score-tips-header">
        <div className="off-score-tips-title" onClick={handleToggle} style={{ cursor: 'pointer', flex: 1 }}>
          📝 {t('tips.generalTips')} 
          {tips.length > 0 && (
            <span style={{ 
              background: '#4f46e5', 
              color: 'white', 
              borderRadius: '12px', 
              padding: '2px 8px', 
              fontSize: '11px',
              marginLeft: '8px'
            }}>
              {tips.length}
            </span>
          )}
        </div>
        
        {/* Management button for users with tip management permissions */}
        {canCreateTips && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent header toggle
              handleCreateTip();
            }}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginRight: '8px'
            }}
            title={t('tips.createOffScoreTip')}
          >
            + {t('add')}
          </button>
        )}
        
        <div className={`off-score-tips-toggle ${!collapsed ? 'expanded' : ''}`} onClick={handleToggle} style={{ cursor: 'pointer' }}>
          ▼
        </div>
      </div>

      {!collapsed && (
        <div className="off-score-tips-content">
          {loading ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#6b7280',
              fontSize: '14px'
            }}>
              {t('tips.loading')}
            </div>
          ) : tips.length === 0 ? (
            <div className="off-score-tips-empty">
              {t('tips.noGeneralTips')}
            </div>
          ) : (
            tips.map(tip => (
              <div key={tip.id} className="off-score-tip-item">
                <div className="off-score-tip-header">
                  <div className="off-score-tip-meta">
                    <span>{getCreatorName(tip)}</span>
                    <span>•</span>
                    <span>{formatDate(tip.created_at)}</span>
                    <span>•</span>
                    <span>{getTargetingDescription(tip)}</span>
                  </div>
                  
                  {/* Edit button for users with tip management permissions */}
                  {canPerformAction(user, 'edit-tip') && (
                    <button
                      onClick={() => handleEditTip(tip)}
                      style={{
                        background: '#065f46',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        marginLeft: '8px'
                      }}
                      title={t('tips.editTip')}
                    >
                      ✏️ {t('edit')}
                    </button>
                  )}
                </div>
                
                <div className="off-score-tip-content">
                  <TipContentRenderer 
                    content={tip.content}
                    contentHtml={tip.content_html}
                    musicxmlId={tip.musicxml_id}
                  />
                </div>
                
                {musicxmlId && tip.musicxml_id && tip.musicxml_id !== musicxmlId && (
                  <div className="off-score-tip-footer">
                    {t('tips.fromScore')}: {tip.musicxml_id}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Off-Score Tip Dialog */}
      <OffScoreTipDialog
        isOpen={showTipDialog}
        onClose={() => {
          setShowTipDialog(false);
          setEditingTip(null);
        }}
        onCreateTip={handleTipSaved}
        editingTip={editingTip}
        currentFileName={musicxmlId}
      />
    </div>
  );
};

export default OffScoreTipsDisplay;